<template>
	<div>
		<CModal
			:show.sync="isShow"
			:close-on-backdrop="false"
			centered
		>
			<template #header-wrapper>
				<ModalHeaderWrapper
					:title="title"
					@onClose="close"
				/>
			</template>
			<template #default>
				<BaseLoading v-if="isMainLoading" />
				<div v-else>
					<form @submit.prevent="handleEnterSearch">
						<CInput
							v-model.trim="searchKeyword"
							placeholder="Search by navigation menu"
							class="flex-fill"
							autocomplete="off"
							data-test-id="input-navigation-menu"
						>
							<template #prepend-content>
								<CIcon class="cil-magnifying-glass" name="cil-magnifying-glass" />
							</template>
						</CInput>
						<input type="submit" style="display: none;">
					</form>
					<div v-show="filteredList.length" class="list">
						<DynamicScroller
							:items="filteredList"
							:min-item-size="20"
							key-field="id"
							class="scroller list-select"
							@scroll.native="handleScroll"
						>
							<template v-slot="{ item, index, active }">
								<DynamicScrollerItem
									:item="item"
									:active="active"
									:data-index="index"
									class="list-item"
								>
									<div class="d-flex flex-fill">
										<CInputCheckbox
											:id="`modal_navigation_menu_${item.id}`"
											:checked="checkItemIsSelected(item.id)"
											custom
											class="list-checkbox overflow-hidden"
											@update:checked="handleCheckedItem(item)"
										>
											<template #label>
												<label :for="`modal_navigation_menu_${item.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
													<span class="color-black">{{ item.name }}</span>
												</label>
											</template>
										</CInputCheckbox>
									</div>
								</DynamicScrollerItem>
							</template>
							<template #after>
								<BaseLoading v-if="isLoadingMore" />
							</template>
						</DynamicScroller>
					</div>
				</div>
			</template>
			<template #footer>
				<BaseModalFooter
					:total-select="selectedList.length"
					@onClear="handleClear"
					@onCancel="handleCancel"
					@onConfirm="handleSubmit"
				/>
			</template>
		</CModal>
	</div>
</template>


<script>
import { mapState, mapActions } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import debounce from 'lodash/debounce';

import BaseModalFooter from '@/components/BaseModalFooter.vue';
import BaseLoading from '@/components/BaseLoading.vue';
import ModalHeaderWrapper from './ModalHeaderWrapper.vue';

import { cloneDeep } from '../assets/js/helpers';

import { NAVIGATION_MENU_CONTENT_TYPE } from '../enums/navigationMenu';

export default {
	name: 'ModalNavigationMenu',
	components: {
		DynamicScroller,
		DynamicScrollerItem,
		ModalHeaderWrapper,
		BaseModalFooter,
		BaseLoading,
	},
	props: {
		title: {
			type: String,
			default: 'Select Navigation Menu',
		},
		navigationMenuIds: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			isShow: false,
			isMainLoading: false,
			searchKeyword: '',
			selectedList: [],
			isLoadingMore: false,
		};
	},
	computed: {
		...mapState('navigationMenu', {
			isLoading: (state) => state.list.isLoading,
			list: (state) => state.list.data,
			meta: (state) => state.list.meta,
		}),
		filteredList() {
			return this.list;
		},
		hasNextPage() {
			if (!this.meta?.currentPage || !this.meta?.lastPage) {
				return false;
			}

			return this.meta.currentPage < this.meta.lastPage;
		},
	},
	watch: {
		searchKeyword(val) {
			this.debouncedSearch(val);
		},
	},
	created() {
		this.debouncedSearch = debounce(async (val) => {
			if (val.length > 2 || val.length === 0) {
				this.isMainLoading = true;
				await this.fetchNavigationMenus(1);
				this.isMainLoading = false;
			}
		}, 1000);
	},
	methods: {
		...mapActions({
			getNavigationMenus: 'navigationMenu/getNavigationMenus',
		}),

		async open(params) {
			if (params?.isFetch || false) {
				this.isMainLoading = true;
				await this.fetchNavigationMenus();
				this.isMainLoading = false;
			}
			if (params?.selectedList?.length) {
				this.selectedList = cloneDeep(params.selectedList);
			}
			this.isShow = true;
		},
		close() {
			this.isShow = false;
		},
		checkItemIsSelected(val) {
			return this.selectedList.some((item) => item.id === val);
		},
		handleCheckedItem(checkedItem) {
			if (!checkedItem?.id) {
				return;
			}
			const isSelected = this.selectedList.some((item) => item.id === checkedItem.id);

			if (!isSelected) {
				this.addToSelectedList(checkedItem);
			} else {
				this.removeFromSelectedList(checkedItem);
			}
		},
		addToSelectedList(item) {
			if (!item) {
				return;
			}
			this.selectedList.push(item);
		},

		removeFromSelectedList(oldItem) {
			if (!oldItem) {
				return;
			}
			this.selectedList = this.selectedList.filter((item) => item.id !== oldItem.id);
		},

		handleCancel() {
			this.close();
		},
		handleClear() {
			this.selectedList = [];
		},
		handleSubmit() {
			this.isShow = false;
			this.$emit('onConfirm', this.selectedList);
		},

		async fetchNavigationMenus(page = 1, isAppend = false) {
			const params = {
				platform: 'web',
				content_type: NAVIGATION_MENU_CONTENT_TYPE.menu,
				page,
				isAppend,
				q: this.searchKeyword,
			};
			await this.getNavigationMenus(params);
		},
		async handleEnterSearch() {
			if (this.debouncedSearch) {
				this.debouncedSearch.cancel();
			}
			this.isMainLoading = true;
			await this.fetchNavigationMenus(1);
			this.isMainLoading = false;
		},
		handleScroll(event) {
			const { scrollTop, clientHeight, scrollHeight } = event.target;
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				this.loadMore();
			}
		},
		async loadMore() {
			if (this.isLoading
				|| this.isLoadingMore
				|| !this.hasNextPage) {
				return;
			}

			this.isLoadingMore = true;
			await this.fetchNavigationMenus(this.meta.currentPage + 1, true);
			this.isLoadingMore = false;
		},
	},
};
</script>

<style lang="scss" scoped>
	.scroller {
		height: 100%;
	}

	.list {
		margin: 0 -#{rem(16)} -#{rem(16)};
	}

	.list-select {
		height: rem(360);
		overflow: auto !important;
		list-style: none;
		padding: 0;
		margin: 0;

		.list-item {
			padding: rem(6) rem(16);

			label {
				margin-top: rem(3);
				cursor: pointer;
			}
		}
	}
</style>
